if (!customElements.get('app-banner')) {
  class AppBanner extends HTMLElement {
    constructor() {
      super();
      this.boundHandleClose = null;
    }

    connectedCallback() {
      // Handle dismissal state
      const persistDismissal = this.dataset.persistDismissal === "true";
      const dismissalKey = "app-banner-dismissed";

      // Check if banner was previously dismissed
      if (persistDismissal && sessionStorage.getItem(dismissalKey)) {
        this.classList.add('app-banner--dismissed');
        return;
      }

      // Bind methods for cleanup
      this.boundHandleClose = this.handleClose.bind(this);

      // Close button handler
      const closeButton = this.querySelector(".app-banner__close-button");
      if (closeButton) {
        closeButton.addEventListener("click", this.boundHandleClose);
      }

      // Process app link elements (empty <a> tags with data attributes)
      this.processAppLinks();

      // Check if content is effectively empty (only empty elements)
      // If so, don't render the banner to avoid empty space
      if (this.hasEmptyContent()) {
        this.classList.add('app-banner--dismissed');
        return;
      }

      this.classList.add("app-banner--ready");

      // Note: Header height tracking is now handled by ResizeObserver in global.js
      // The ResizeObserver automatically detects when #site-header dimensions change
      // (including when app banner collapses/expands via CSS Grid), eliminating the
      // need for scroll-based polling. This is more efficient as it only fires when
      // dimensions actually change, not on every scroll event.
    }

    disconnectedCallback() {
      // Remove close button listener
      const closeButton = this.querySelector(".app-banner__close-button");
      if (closeButton && this.boundHandleClose) {
        closeButton.removeEventListener("click", this.boundHandleClose);
      }

      // Clear references
      this.boundHandleClose = null;
      this.boundUpdateHeaderHeight = null;
      this.boundHandleScrollStateChange = null;
    }

    updateHeaderHeight() {
      // Update header height variable to account for banner collapse/expand
      // CSS Grid handles the visual collapse automatically, but JavaScript needs
      // the actual computed height for positioning drawers and calculating offsets
      setElementHeightVariable('site-header', '--header-height');
    }

    handleScrollStateChange() {
      // Update header height when scroll state changes
      this.updateHeaderHeight();
    }

    handleClose() {
      // Prevent multiple clicks
      if (this.classList.contains('app-banner--dismissed')) {
        return;
      }

      const section = this.closest('.shopify-section--app-banner');
      if (!section) return;

      const persistDismissal = this.dataset.persistDismissal === "true";

      // Mark as dismissed to prevent multiple clicks
      this.classList.add('app-banner--dismissed');

      // Handle completion after transition
      let transitionCompleted = false;

      const completeDismissal = () => {
        if (transitionCompleted) return;
        transitionCompleted = true;

        // Note: Header height update is handled automatically by ResizeObserver in global.js
        // When CSS Grid collapses the banner, ResizeObserver detects the #site-header
        // dimension change and updates --header-height accordingly

        // Set sessionStorage if persistence is enabled
        if (persistDismissal) {
          sessionStorage.setItem("app-banner-dismissed", "true");
        }
      };

      // Listen for transition end
      const handleTransitionEnd = (event) => {
        // Only handle transition if it's our banner section and the right property
        if (event.target !== section) return;
        if (event.propertyName !== 'max-height' && event.propertyName !== 'opacity') return;

        section.removeEventListener('transitionend', handleTransitionEnd);
        completeDismissal();
      };

      section.addEventListener('transitionend', handleTransitionEnd);

      // Fallback timeout in case transitionend doesn't fire (e.g., transitions disabled)
      setTimeout(() => {
        completeDismissal();
      }, 600); // Slightly longer than CSS transition (500ms)
    }

    processAppLinks() {
      // Find all empty app link elements (populated by Liquid template)
      const appLinks = this.querySelectorAll(".app-banner__link:empty");

      if (appLinks.length === 0) {
        return;
      }

      // Device detection: iOS/iPadOS vs Android
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
      const isAndroid = /Android/.test(userAgent);

      appLinks.forEach((link) => {
        const appStoreLink = link.dataset.appStoreLink || "";
        const googlePlayLink = link.dataset.googlePlayLink || "";
        const appLinkText = link.dataset.appLinkText || "";

        // Determine which link to use based on device
        let targetLink = "";
        if (isIOS && appStoreLink) {
          targetLink = appStoreLink;
        } else if (isAndroid && googlePlayLink) {
          targetLink = googlePlayLink;
        }

        // Populate link if valid, otherwise remove it
        if (targetLink && appLinkText) {
          link.href = targetLink;
          link.textContent = appLinkText;
        } else {
          link.remove();
        }
      });
    }

    hasEmptyContent() {
      // Check if the content element only contains empty elements
      const contentElement = this.querySelector(".app-banner__content");
      if (!contentElement) {
        return true;
      }

      // Get text content and trim whitespace
      const textContent = contentElement.textContent.trim();
      if (textContent.length > 0) {
        return false;
      }

      // Check if there are any non-empty child elements
      // An element is considered non-empty if it has:
      // - Non-whitespace text content, OR
      // - Child elements that themselves have content
      const hasNonEmptyElements = Array.from(contentElement.children).some((child) => {
        // Check if element has non-whitespace text content
        if (child.textContent.trim().length > 0) {
          return true;
        }
        // Check if element has any non-empty child elements
        return child.children.length > 0 && Array.from(child.children).some(
          (grandchild) => grandchild.textContent.trim().length > 0 || grandchild.children.length > 0
        );
      });

      return !hasNonEmptyElements;
    }
  }

  customElements.define('app-banner', AppBanner);
}
